// Who is signed in, and as whom they act.
//
// A team member (userAccountType "Team") is a technician or helper of a
// service partner (userAccountType "Professional") with their own login. They
// work the SP's screens AS the SP: jobs, chat messages and responses belong to
// the SP, so wherever an SP screen uses "my userId" to mean "the SP" (chat
// alignment, respondUserId, job.userId / proposedBy comparisons, message
// templates) a team member must use the SP's id - spIdOf(user). Firestore
// listeners (appNotification/{userId}) and the notification APIs keep the
// member's own userId.
//
// Every helper takes the UserContext value (or anything with the same keys:
// userAccountType, userId, spUserId, teamRole, isSelfManaged). Passing a bare
// account type string also works for the type checks.

const typeOf = (user) => (typeof user === 'string' ? user : user?.userAccountType)

export const TEAM_ACCOUNT = 'Team'
export const TEAM_ROLE_LEAD = 'LEAD'

export const isProfessional = (user) => typeOf(user) === 'Professional'

export const isTeamAccount = (user) => typeOf(user) === TEAM_ACCOUNT

// Screens built for the service partner that team members also use (jobs,
// calendar, chat, the request feed). Payout, subscription, earnings, chips,
// business profile, settings, preferences and team management stay
// Professional-only: check isProfessional for those.
export const isSpUi = (user) => isProfessional(user) || isTeamAccount(user)

// The id that means "the service partner" on SP screens. undefined until the
// account type is known, so a team member's screens never fire with their own
// id first: guard on it like on userId.
export const spIdOf = (user) => {
    if (!user || typeof user === 'string' || !user.userAccountType) return undefined
    return isTeamAccount(user) ? (user.spUserId ?? undefined) : user.userId
}

// True when id is the signed-in SP, or for a team member, their SP or themselves
export const isMeAsSp = (user, id) => {
    if (id === undefined || id === null || id === '') return false
    const value = String(id)
    const sp = spIdOf(user)
    return (sp !== undefined && String(sp) === value) || (user?.userId !== undefined && String(user.userId) === value)
}

export const isSelfManagedTeam = (user) => isTeamAccount(user) && !!user?.isSelfManaged

export const isManagedTeam = (user) => isTeamAccount(user) && !user?.isSelfManaged

export const isTeamLead = (user) => isTeamAccount(user) && user?.teamRole === TEAM_ROLE_LEAD

// Accepting requests, rescheduling and asking for a cancellation: the SP, or a
// member of a self-managed team (managed teams leave those to the SP)
export const canActOnBookings = (user) => !isTeamAccount(user) || isSelfManagedTeam(user)

// Routes a team member never sees (money, the SP's business profile and
// settings, customer-only screens). Matched as prefixes of router.route.
export const TEAM_BLOCKED_ROUTES = [
    '/account/payout', '/account/subscription', '/account/settings', '/account/preferences',
    '/account/notifications',
'/account/sp-team', '/account/earnings', '/account/referral', '/account/payment',
    '/account/my-packages', '/account/change-password', '/account/privacy',
    '/stripe-account', '/rewards', '/make-payment', '/booking', '/history', '/corporate',
    '/home/packages', '/home/buzz', '/home/deals', '/home/services', '/home/offer',
    '/home/job-invite', '/home/chat', '/home/personal-training', '/home/requests/',
]

// Where a team member on `route` should go instead, or null to stay. Managed
// teams have no request feed, so their home is /jobs.
export const teamRedirectFor = (user, route) => {
    if (!isTeamAccount(user) || !route) return null
    if (TEAM_BLOCKED_ROUTES.some(r => route.startsWith(r))) {
        return '/jobs'
    }
    if (!isSelfManagedTeam(user) && (route === '/home' || route === '/home/requests')) return '/jobs'
    return null
}
